export type PwaInstallGuide = "prompt" | "ios-safari" | "ios-open-in-safari";

export interface PwaInstallEnvironment {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  standalone: boolean;
}

export const PWA_INSTALL_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

const SNOOZED_AT_KEY = "coloca:pwa-install-snoozed-at";

const IOS_NON_SAFARI_MARKERS =
  /CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|DuckDuckGo|YaBrowser|Instagram|FBAN|FBAV|LinkedInApp|Line\/|Snapchat|musical_ly|TikTok|Twitter/;

export function isIosDevice(env: Pick<PwaInstallEnvironment, "userAgent" | "platform" | "maxTouchPoints">) {
  if (/iPhone|iPad|iPod/.test(env.userAgent)) return true;
  // iPadOS asks for the desktop site and reports itself as a Mac; only the touch screen gives it away.
  return env.platform === "MacIntel" && env.maxTouchPoints > 1;
}

export function isIosSafari(userAgent: string) {
  // In-app web views drop the "Safari/" token; other iOS browsers keep it but add their own marker.
  return /Safari\//.test(userAgent) && !IOS_NON_SAFARI_MARKERS.test(userAgent);
}

export function resolvePwaInstallGuide(
  env: PwaInstallEnvironment,
  canPrompt: boolean,
): PwaInstallGuide | null {
  if (env.standalone) return null;
  if (canPrompt) return "prompt";
  if (!isIosDevice(env)) return null;
  return isIosSafari(env.userAgent) ? "ios-safari" : "ios-open-in-safari";
}

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

export function isPwaInstallSnoozed(storage: ReadableStorage | null, now: number) {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(SNOOZED_AT_KEY) ?? null;
  } catch {
    return false;
  }
  const snoozedAt = Number(raw);
  if (!raw || !Number.isFinite(snoozedAt)) return false;
  return now - snoozedAt < PWA_INSTALL_SNOOZE_MS;
}

export function snoozePwaInstall(storage: WritableStorage | null, now: number) {
  try {
    storage?.setItem(SNOOZED_AT_KEY, String(now));
  } catch {
    // Without storage the banner simply comes back on the next visit.
  }
}

export function readPwaInstallEnvironment(): PwaInstallEnvironment {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    standalone: nav.standalone === true || window.matchMedia("(display-mode: standalone)").matches,
  };
}

export function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
